import React from "react";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { MantineProvider } from "@mantine/core";

import useAuthStore from "./store/authStore";
import {
    applyAuthHeader,
    getAccessToken,
    getStoredUser,
    isAccessTokenExpired,
    isAuthApiError,
    isAuthSessionEndpoint,
    refreshAccessTokenOnce,
} from "./utils/authSession";
import { parseApiResponse } from "./utils/apiParams";
import {
    checkAppVersionIfStale,
    cleanReloadTimeParam,
    handleAppVersionFromResponse,
} from "./utils/appVersion";

import RoutesList from "./components/routes.list.component";
import Preloader from "./components/preloader/preloader.component";
import ToTopButton from "./components/to_top_button/to.top.button.component";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "./styles/App.scss";

const App = () => {
    const { setUser, logout, restoreSession } = useAuthStore();

    const [timer, setTimer] = React.useState(1500);
    const [app, setApp] = React.useState(false);

    const fetchData = async () => {
        window.localStorage.removeItem("user");
        applyAuthHeader();

        axios.interceptors.response.use(
            async (response) => {
                if (typeof response.data === "string") {
                    response.data = parseApiResponse(response.data);
                }

                handleAppVersionFromResponse(response);

                const requestUrl = response.config?.url || "";

                if (
                    isAuthApiError(response?.data) &&
                    !isAuthSessionEndpoint(requestUrl)
                ) {
                    const originalRequest = response.config;

                    if (!originalRequest._retry) {
                        originalRequest._retry = true;
                        const user = await refreshAccessTokenOnce();

                        if (user) {
                            setUser(user);
                            originalRequest.headers =
                                originalRequest.headers || {};
                            originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
                            applyAuthHeader();
                            return axios(originalRequest);
                        }
                    }

                    const token = getAccessToken();
                    const stored = getStoredUser();

                    if (
                        token &&
                        stored &&
                        !isAccessTokenExpired(token) &&
                        !originalRequest._tokenRetry
                    ) {
                        originalRequest._tokenRetry = true;
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        applyAuthHeader();
                        return axios(originalRequest);
                    }

                    await logout();
                    return Promise.reject(
                        new Error(response?.data?.error_text || "auth"),
                    );
                }
                return response;
            },
            (error) => {
                return Promise.reject(error.message);
            },
        );

        cleanReloadTimeParam();

        try {
            await checkAppVersionIfStale(window.global.baseUrl);
            await restoreSession();
        } catch (e) {
            console.error("restoreSession:", e);
        } finally {
            setApp(true);
            setTimer(0);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const AnimatedPageTransition = ({ children }) => {
        return (
            <AnimatePresence mode="wait">
                <Preloader loading={timer > 0}>{children}</Preloader>
            </AnimatePresence>
        );
    };

    return (
        <MantineProvider>
            {app && (
                <BrowserRouter>
                    <AnimatedPageTransition>
                        <RoutesList />
                        <ToTopButton />
                    </AnimatedPageTransition>
                </BrowserRouter>
            )}
        </MantineProvider>
    );
};

export default App;
